/*
  Hive predictive search (header dropdown).

  Based on Dawn's predictive-search.js, rewritten for latency:

  1. Dawn debounces every keystroke by 300ms in search-form.js and only then starts the
     request, so the first results land ~550ms after you stop typing. This file owns the
     input event with a 90ms debounce instead, and fires straight away when the answer is
     already cached.
  2. Exactly one request is ever in flight. While someone is still typing, a request for a
     prefix of what they now have is left to finish (its results are useful and worth
     caching) and the newest term is queued behind it; anything else is aborted. A single
     letter only goes out after a 300ms pause and never holds up a longer term, since its
     results are rarely what anyone wants. Dawn fired a request per keystroke with no
     cancelling at all, so a slow early response could overwrite a newer one.
  3. While the exact results are loading, the dropdown immediately shows the closest cached
     prefix ("narut" -> the cached "naru" results), so something correct is on screen within
     a frame instead of leaving the panel empty.
  4. Identical markup is never re-rendered, and the cache is capped (LRU) so long sessions
     don't grow without limit.
*/

const HIVE_SEARCH_DEBOUNCE = 90;
const HIVE_SEARCH_CACHE_MAX = 40;
const HIVE_SEARCH_MIN_PREFIX = 2;

class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.cacheOrder = [];
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';
    this.inFlightTerm = null;
    this.queuedTerm = null;
    this.renderedTerm = null;
    this.renderedMarkup = null;
    this.buttonTerm = null;
    this.debounceTimer = null;

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    // Our own fast path. SearchForm also listens with a 300ms debounce; onChange below
    // only syncs the reset button, so that call never re-runs the search.
    this.input.addEventListener('input', this.onInput.bind(this));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  cacheKey(term) {
    return term.replace(/\s+/g, '-').toLowerCase();
  }

  cacheGet(term) {
    return this.cachedResults[this.cacheKey(term)];
  }

  cacheSet(term, markup) {
    const key = this.cacheKey(term);
    const entry = { markup, term };

    this.allPredictiveSearchInstances.forEach((instance) => {
      if (!instance.cachedResults) return;
      instance.cachedResults[key] = entry;
      if (!instance.cacheOrder) instance.cacheOrder = [];
      const position = instance.cacheOrder.indexOf(key);
      if (position !== -1) instance.cacheOrder.splice(position, 1);
      instance.cacheOrder.push(key);
      while (instance.cacheOrder.length > HIVE_SEARCH_CACHE_MAX) {
        delete instance.cachedResults[instance.cacheOrder.shift()];
      }
    });
  }

  // Exact hit, or the longest cached prefix of this term - "narut" happily shows the
  // cached results for "naru" while its own request is still on the wire.
  bestCached(term) {
    const exact = this.cacheGet(term);
    if (exact) return { entry: exact, exact: true };

    for (let length = term.length - 1; length >= HIVE_SEARCH_MIN_PREFIX; length--) {
      const entry = this.cacheGet(term.slice(0, length));
      if (entry) return { entry, exact: false };
    }

    return null;
  }

  onInput() {
    this.toggleResetButton();

    const newSearchTerm = this.getQuery();
    this.searchTerm = newSearchTerm;

    if (!newSearchTerm.length) {
      clearTimeout(this.debounceTimer);
      this.abortInFlight();
      this.close(true);
      return;
    }

    const cached = this.bestCached(newSearchTerm);

    // Paint whatever we already have, this frame.
    if (cached) this.renderSearchResults(cached.entry, newSearchTerm, cached.exact);

    clearTimeout(this.debounceTimer);

    if (cached && cached.exact) return; // nothing left to fetch

    // A single letter is only worth fetching once they pause on it.
    if (newSearchTerm.length < HIVE_SEARCH_MIN_PREFIX) {
      this.debounceTimer = setTimeout(() => this.getSearchResults(newSearchTerm), 300);
      return;
    }

    this.setAttribute('loading', true);

    // If the request already running is for a prefix of this term, let it finish - its
    // results are worth showing and caching - and run this term straight after. A
    // single-letter request is not worth waiting for; getSearchResults aborts it.
    if (
      this.inFlightTerm &&
      this.inFlightTerm.length >= HIVE_SEARCH_MIN_PREFIX &&
      newSearchTerm.startsWith(this.inFlightTerm)
    ) {
      this.queuedTerm = newSearchTerm;
      return;
    }

    this.debounceTimer = setTimeout(() => this.getSearchResults(newSearchTerm), HIVE_SEARCH_DEBOUNCE);
  }

  // SearchForm's debounced listener lands here. onInput already owns the input event, so
  // re-running it would only reset the debounce and repeat the live-region announcement.
  onChange() {
    this.toggleResetButton();
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.renderedTerm = null;
      clearTimeout(this.debounceTimer);
      this.abortInFlight();
      this.close(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from the other search input, treat it as a user change
      this.onInput();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  // Keeps the "Search for ..." button in step with what is in the input, even when the
  // markup on screen was rendered for a shorter term. Works from the term the button shows
  // now (buttonTerm), not the one the markup was rendered for - after one rewrite those
  // differ, and replacing the stale one garbled "naru" -> "narut" -> "narutot".
  updateSearchForTerm(previousTerm, newTerm) {
    const prevTerm = this.buttonTerm;
    if (!prevTerm || prevTerm === newTerm) return;
    const el = this.querySelector('[data-predictive-search-search-for-text]');
    if (!el) return;
    // textContent rather than innerText, which forces a layout.
    const text = el.textContent;
    // The term sits last, inside the quotes. The first match could be in the label
    // itself: "ch" -> "cha" would otherwise give 'Searcha for "ch"'.
    const i = text.lastIndexOf(prevTerm);
    if (i === -1) return;
    el.textContent = text.slice(0, i) + newTerm + text.slice(i + prevTerm.length);
    this.buttonTerm = newTerm;
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null
    );
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

  abortInFlight() {
    this.abortController.abort();
    this.abortController = new AbortController();
    this.inFlightTerm = null;
    this.queuedTerm = null;
    this.removeAttribute('loading');
  }

  // Runs whatever the person typed while the last request was on the wire.
  runQueued() {
    const queued = this.queuedTerm;
    this.queuedTerm = null;
    if (!queued || queued !== this.getQuery()) return;
    if (this.cacheGet(queued)) {
      this.renderSearchResults(this.cacheGet(queued), queued, true);
      return;
    }
    this.getSearchResults(queued);
  }

  getSearchResults(searchTerm) {
    if (!searchTerm.length) return;

    const cached = this.cacheGet(searchTerm);
    if (cached) {
      this.renderSearchResults(cached, searchTerm, true);
      return;
    }

    // One request at a time: drop whatever the previous keystroke started.
    this.abortInFlight();
    this.inFlightTerm = searchTerm;
    this.setLiveRegionLoadingState();

    const signal = this.abortController.signal;
    const url = `${routes.predictive_search_url}?q=${encodeURIComponent(
      searchTerm
    )}&resources[limit]=6&resources[limit_scope]=each&section_id=predictive-search`;

    fetch(url, { signal })
      .then((response) => {
        if (!response.ok) {
          const error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search').innerHTML;

        this.cacheSet(searchTerm, resultsMarkup);
        if (this.inFlightTerm === searchTerm) this.inFlightTerm = null;

        const currentTerm = this.getQuery();
        const entry = { markup: resultsMarkup, term: searchTerm };

        if (currentTerm === searchTerm) {
          this.renderSearchResults(entry, currentTerm, true);
        } else if (
          currentTerm.startsWith(searchTerm) &&
          (!this.renderedTerm || searchTerm.length > this.renderedTerm.length)
        ) {
          // They typed on: show these now, they are the closest thing we have - but never
          // replace results already rendered for a longer (closer) prefix.
          this.renderSearchResults(entry, currentTerm, false);
        }

        this.runQueued();
      })
      .catch((error) => {
        if (this.inFlightTerm === searchTerm) this.inFlightTerm = null;
        if (error?.name === 'AbortError' || error?.code === 20) return; // superseded by a newer query
        // A longer term typed while this one failed still gets its own request.
        const takeover = this.queuedTerm && this.queuedTerm === this.getQuery();
        this.runQueued();
        if (takeover) return;
        this.close();
        throw error;
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    if (!this.statusElement) return;
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  // entry: { markup, term } - term is what the markup was rendered for, which can be a
  // shorter prefix of what is in the input right now.
  renderSearchResults(entry, currentTerm, isExact) {
    const markup = typeof entry === 'string' ? entry : entry.markup;
    const markupTerm = typeof entry === 'string' ? currentTerm : entry.term;

    if (markup !== this.renderedMarkup) {
      this.predictiveSearchResults.innerHTML = markup;
      this.renderedMarkup = markup;
      this.renderedTerm = markupTerm;
      this.buttonTerm = markupTerm;
    }

    // Compared with what the button shows, not the markup's term: backspacing from "narut"
    // to the cached "naru" keeps the same markup but the button still reads "narut".
    if (currentTerm && this.buttonTerm !== currentTerm) {
      this.updateSearchForTerm(markupTerm, currentTerm);
    }

    this.setAttribute('results', true);

    if (isExact) {
      this.renderedTerm = currentTerm || markupTerm;
      this.setLiveRegionResults();
    }

    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    const countElement = this.querySelector('[data-predictive-search-live-region-count-value]');
    if (countElement) this.setLiveRegionText(countElement.textContent);
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight - document.querySelector('.section-header')?.getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    if (!this.isOpen) {
      // The Hive header panel forces max-height: none in CSS, so skip the forced layout there.
      if (!this.closest('.hsearch')) {
        this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
      }
      this.setAttribute('open', true);
      this.input.setAttribute('aria-expanded', true);
      this.isOpen = true;
    }
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
      this.renderedMarkup = null;
      this.renderedTerm = null;
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.isOpen = false;
    this.predictiveSearchResults.removeAttribute('style');
  }
}

customElements.define('predictive-search', PredictiveSearch);
